import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Paperclip, ArrowUp, Video, Image as ImageIcon, Palette, X, Loader2,
  Sparkles, Coins, MessageSquare, Plus, Trash2, Pin, PinOff,
} from "lucide-react";
import Branding from "@/pages/Branding";
import StudioPhotoEditor from "@/pages/StudioPhotoEditor";

const TABS = [
  { id: "video", label: "Video",         icon: Video,      enabled: true  },
  { id: "photo", label: "Photo editing", icon: ImageIcon,  enabled: true  },
  { id: "brand", label: "Brand",         icon: Palette,    enabled: true  },
];

const TIER_CREDITS = { "budget-tier": 20, "premium-tier": 35, "ultra-tier": 80, "ffmpeg-preview": 0 };
const TIER_LABEL   = { "budget-tier": "Budget", "premium-tier": "Premium", "ultra-tier": "Ultra", "ffmpeg-preview": "Preview" };

const WELCOME = {
  role: "assistant",
  text: "Hi! Ik ben je video-assistent. Beschrijf de video die je wilt maken, voeg 1-30 foto's toe met het 📎-icoon, en ik regel de rest — inclusief de beste tier, aspect ratio en stijl.",
};

/** Strip common markdown emphasis so the plain <div> renders cleanly.
 *  Applied defensively — the system prompt already asks Claude for plain text.
 */
function _stripMarkdown(t) {
  if (!t) return t;
  return t
    .replace(/\*\*(.+?)\*\*/g, "$1")   // **bold**
    .replace(/(^|\s)\*(\S(?:.*?\S)?)\*(?=\s|$)/g, "$1$2")  // *italic*
    .replace(/^#{1,6}\s+/gm, "")        // headings
    .replace(/^\s*[-*]\s+/gm, "· ");    // bullet dashes
}

export default function Studio() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshBalance } = useAuth();

  const [tab, setTab] = useState("video");
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null); // null = ephemeral, gets set on first submit
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [attached, setAttached] = useState([]); // [{file,url}]
  const [busy, setBusy] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectState, setProjectState] = useState(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const saveTimerRef = useRef(null);

  // ---------- Sessions ----------
  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ---------- URL-driven tab + session (used by the Photo Editing hand-off) ----------
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && ["video", "photo", "brand"].includes(t)) setTab(t);
    const sid = searchParams.get("session");
    if (sid && sid !== sessionId) {
      loadSession(sid);
      // Clear the ?session= param so we don't re-load on every re-render.
      const next = new URLSearchParams(searchParams);
      next.delete("session");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const newChat = () => {
    setSessionId(null);
    setMessages([WELCOME]);
    setActiveProjectId(null);
    setProjectState(null);
    setInput("");
    setAttached((a) => { a.forEach((x) => URL.revokeObjectURL(x.url)); return []; });
  };

  const loadSession = async (id) => {
    try {
      const { data } = await api.get(`/chat/sessions/${id}`);
      setSessionId(data.id);
      setMessages(data.messages?.length ? data.messages : [WELCOME]);
      setActiveProjectId(data.project_id || null);
      setSessionsOpen(false);
    } catch { toast.error("Kon sessie niet laden"); }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Chat verwijderen?")) return;
    await api.delete(`/chat/sessions/${id}`);
    if (id === sessionId) newChat();
    loadSessions();
  };

  const togglePin = async (s, e) => {
    e?.stopPropagation();
    await api.patch(`/chat/sessions/${s.id}`, { pinned: !s.pinned });
    loadSessions();
  };

  // The agent turn persists messages server-side, so the client no longer
  // debounces its own save. We only need this useEffect to be able to load a
  // remote session (see loadSession) — the write path is server-owned.

  // ---------- Scroll ----------
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, projectState]);

  const pushMsg = (role, text, extra = {}) => setMessages((m) => [...m, { role, text, ...extra }]);

  // ---------- Files ----------
  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attached.length + files.length > 30) { toast.error("Max 30 foto's"); return; }
    setAttached((a) => [...a, ...files.map((f) => ({ file: f, url: URL.createObjectURL(f) }))]);
    e.target.value = "";
  };
  const removeAttached = (idx) => {
    setAttached((a) => {
      const c = [...a]; URL.revokeObjectURL(c[idx].url); c.splice(idx, 1); return c;
    });
  };

  // ---------- Costing (client-side hint only; real charge is server-side per agent tool) ----------
  const potentialCredits = attached.length * (TIER_CREDITS["premium-tier"] || 0);
  void potentialCredits; void TIER_LABEL; // exposed for future UI

  // ---------- Submit — real Claude Sonnet agent flow ----------
  const submit = async () => {
    if (tab !== "video") return;
    const prompt = input.trim();
    if (!prompt && !attached.length) {
      pushMsg("assistant", "Beschrijf eerst wat je wilt zien of voeg foto's toe.");
      return;
    }

    const localAttached = attached;
    pushMsg("user", prompt, { photos: localAttached.map((a) => a.url) });
    setInput("");
    setBusy(true);

    // Optimistic pending bubble the agent will replace.
    let pendingIdx = -1;
    setMessages((m) => {
      pendingIdx = m.length;
      return [...m, { role: "assistant", text: "🤖 De agent denkt na…", pending: true }];
    });
    const updatePending = (text, extra = {}) => setMessages((m) => {
      if (pendingIdx < 0 || pendingIdx >= m.length) return [...m, { role: "assistant", text, ...extra }];
      const c = [...m]; c[pendingIdx] = { role: "assistant", text, ...extra }; return c;
    });

    try {
      // 1) Make sure we have a session id — the agent turn will create one
      //    lazily if we don't pass one, but we need it up-front so /agent/upload
      //    can stage photos to the right folder.
      let sid = sessionId;
      if (!sid) {
        const { data: s } = await api.post("/chat/sessions", {
          tab: "video",
          title: prompt.slice(0, 60) || "New chat",
        });
        sid = s.id;
        setSessionId(sid);
      }

      // 2) Stage photos (if any) to the session's staging folder.
      if (localAttached.length) {
        const fd = new FormData();
        fd.append("session_id", sid);
        for (const a of localAttached) fd.append("files", a.file);
        await api.post("/agent/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttached([]);
      }

      // 3) Stream the agent turn via SSE. This gives us live "typewriter"
      //    output AND keeps the connection alive so Cloudflare (~100s timeout)
      //    doesn't kill long tool-calling turns.
      const token = localStorage.getItem("co_token");
      const resp = await fetch(`${API_BASE}/agent/turn/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sid, message: prompt }),
      });
      if (!resp.ok || !resp.body) {
        const detail = await resp.text().catch(() => "");
        throw new Error(detail || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let liveText = "";
      let createdProjectId = null;

      // Kick the pending bubble from "🤖 De agent denkt na…" to empty as soon
      // as the first token arrives — otherwise the two texts overlap visually.
      let firstToken = true;

      // Read the SSE stream.
      // Each event = "data: {json}\n\n"
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!raw.startsWith("data:")) continue;
          const jsonStr = raw.slice(5).trim();
          if (!jsonStr) continue;
          let ev;
          try { ev = JSON.parse(jsonStr); } catch { continue; }

          if (ev.type === "session" && ev.session_id) {
            if (!sessionId) setSessionId(ev.session_id);
            sid = ev.session_id;
          } else if (ev.type === "token") {
            if (firstToken) { liveText = ""; firstToken = false; }
            liveText += ev.text || "";
            updatePending(_stripMarkdown(liveText));
          } else if (ev.type === "tool_start") {
            // Show a subtle marker inline while a backend tool is running so
            // the user knows we're doing more than typing.
            const label = ({
              create_video_project: "Project aanmaken…",
              attach_photos_to_project: "Foto's koppelen…",
              start_generation: "Renderen starten…",
              get_project_status: "Status ophalen…",
              get_credit_balance: "Credits checken…",
            })[ev.name] || `${ev.name}…`;
            updatePending(_stripMarkdown(liveText) + `\n\n⚙️ ${label}`);
          } else if (ev.type === "tool_done") {
            // Redraw without the transient tool tag; token stream will keep going.
            updatePending(_stripMarkdown(liveText));
          } else if (ev.type === "final") {
            liveText = ev.assistant_text || liveText;
            createdProjectId = ev.created_project_id || createdProjectId;
            updatePending(_stripMarkdown(liveText) || "Klaar!", {
              projectRef: createdProjectId || undefined,
            });
          } else if (ev.type === "error") {
            updatePending(`Sorry — dat ging niet goed: ${ev.message || "onbekende fout"}`);
          }
        }
      }

      if (createdProjectId) {
        setActiveProjectId(createdProjectId);
        await refreshBalance();
      }
      // Refresh the sessions drawer so the new/updated title appears.
      loadSessions();
    } catch (err) {
      const detail = err?.message || "Iets ging mis";
      updatePending(`Sorry — dat ging niet goed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  // ---------- Polling active project ----------
  useEffect(() => {
    if (!activeProjectId) return;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await api.get(`/projects/${activeProjectId}`);
        if (cancelled) return;
        setProjectState(data);
        if (data.status !== "completed" && data.status !== "failed") {
          timer = setTimeout(poll, 4000);
        }
      } catch { /* ignore */ }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [activeProjectId]);

  const pinnedSessions = sessions.filter((s) => s.pinned);
  const recentSessions = sessions.filter((s) => !s.pinned);

  return (
    <div className="min-h-screen flex" data-testid="studio-page">
      {/* Sessions drawer (video tab only) */}
      {tab === "video" && sessionsOpen && (
        <div className="fixed inset-0 z-[70] bg-black/60" onClick={() => setSessionsOpen(false)}>
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-[#0b0b0d] border-r border-white/10 p-4 overflow-y-auto shadow-2xl"
            data-testid="sessions-drawer"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-wider">Chats</div>
              <button
                onClick={() => { newChat(); setSessionsOpen(false); }}
                data-testid="sessions-new"
                className="text-[11px] font-mono-tech uppercase tracking-wider text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" strokeWidth={2} /> New chat
              </button>
            </div>

            {[["Pinned", pinnedSessions], ["Recent", recentSessions]].map(([label, list]) => (
              list.length > 0 && (
                <div key={label} className="mb-4">
                  <div className="font-mono-tech text-[9px] text-zinc-600 uppercase tracking-wider mb-2 px-1">{label}</div>
                  <div className="space-y-1">
                    {list.map((s) => (
                      <div
                        key={s.id}
                        onClick={() => loadSession(s.id)}
                        data-testid={`session-${s.id}`}
                        className={`group cursor-pointer rounded-lg px-3 py-2 text-sm transition-colors flex items-start gap-2 ${
                          sessionId === s.id ? "bg-amber-500/[0.08] border border-amber-500/30" : "hover:bg-white/[0.03]"
                        }`}
                      >
                        <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sessionId === s.id ? "text-amber-400" : "text-zinc-500"}`} strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-zinc-200 truncate">{s.title}</div>
                          {s.preview && s.preview.trim() && s.preview.trim() !== (s.title || "").trim() && (
                            <div className="text-[10px] text-zinc-500 truncate mt-0.5">{s.preview}</div>
                          )}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 flex-shrink-0">
                          <button onClick={(e) => togglePin(s, e)} className="p-1 rounded hover:bg-white/5" title={s.pinned ? "Unpin" : "Pin"}>
                            {s.pinned
                              ? <PinOff className="w-3 h-3 text-amber-400" strokeWidth={1.75} />
                              : <Pin className="w-3 h-3 text-zinc-400" strokeWidth={1.75} />}
                          </button>
                          <button onClick={(e) => deleteSession(s.id, e)} className="p-1 rounded hover:bg-red-500/20" title="Delete">
                            <Trash2 className="w-3 h-3 text-red-400" strokeWidth={1.75} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}

            {sessions.length === 0 && (
              <div className="text-xs text-zinc-500 px-1 mt-2">Nog geen chats. Stel je eerste vraag om er een te starten.</div>
            )}
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col">
        {/* Hero */}
        <div className="pt-14 pb-6 px-6 md:px-10 text-center max-w-3xl mx-auto w-full">
          <div className="flex items-center justify-center gap-3 mb-3">
            {tab === "video" && (
              <button
                onClick={() => setSessionsOpen(true)}
                data-testid="sessions-toggle"
                className="text-[10px] font-mono-tech uppercase tracking-widest text-zinc-400 hover:text-amber-400 flex items-center gap-1.5"
              >
                <MessageSquare className="w-3 h-3" strokeWidth={1.75} /> Chats
              </button>
            )}
            <div className="font-mono-tech text-[10px] text-amber-400 tracking-widest">STUDIO · MEDIA · AI</div>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-tight">
            What will you <span className="font-bold text-amber-400">build</span> today?
          </h1>
          <p className="text-zinc-400 text-sm mt-3">
            {tab === "video" && "Beschrijf je video, upload je foto's, en de AI regelt de rest."}
            {tab === "photo" && "Bewerk je foto's met AI voordat je ze gebruikt in een video."}
            {tab === "brand" && "Beheer je logo, kleuren en branding — alles wat op je video's verschijnt."}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center px-4 mb-6 overflow-x-auto">
          <div className="inline-flex gap-1 rounded-full bg-[#121214] border border-white/5 p-1 whitespace-nowrap" data-testid="studio-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`studio-tab-${t.id}`}
                  className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                    active ? "bg-amber-500 text-black" : "text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab body */}
        {tab === "brand" && <div className="flex-1"><Branding /></div>}
        {tab === "photo" && <div className="flex-1"><StudioPhotoEditor /></div>}
        {tab === "video" && (
          <div className="flex-1 flex flex-col items-center px-4 pb-4">
            <div ref={scrollRef} className="w-full max-w-3xl flex-1 overflow-y-auto space-y-4 pb-6" data-testid="chat-scroll">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={m.aiSettings ? "chat-ai-settings" : `msg-${m.role}-${i}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                      m.role === "user"
                        ? "bg-amber-500 text-black rounded-br-sm"
                        : "bg-[#121214] text-zinc-100 border border-white/10 rounded-bl-sm chat-bubble-assistant"
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    {(() => {
                      // Prefer transient blob URLs (this session) — but fall back to
                      // persisted filenames when restoring an old chat.
                      const imgs = m.photos && m.photos.length ? m.photos : (m.photo_refs || []);
                      if (!imgs.length) return null;
                      return (
                        <div className="grid grid-cols-4 gap-1 mt-2">
                          {imgs.slice(0, 8).map((u, k) => (
                            <img key={k} src={u.startsWith("blob:") || u.startsWith("http") ? u : fileUrl(u)}
                                 alt="" className="w-full aspect-square object-cover rounded" />
                          ))}
                        </div>
                      );
                    })()}
                    {m.projectRef && (
                      <button
                        onClick={() => nav(`/projects/${m.projectRef}`)}
                        className="mt-3 text-[11px] font-mono-tech uppercase tracking-wider underline hover:no-underline"
                      >
                        Open project detail →
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {projectState && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3" data-testid="live-progress">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono-tech text-[10px] text-amber-400 uppercase tracking-wider">
                      {projectState.status === "completed" ? "Klaar" :
                       projectState.status === "failed" ? "Mislukt" : "Aan het renderen…"}
                    </div>
                    <div className="text-[10px] font-mono-tech text-zinc-500">{projectState.progress ?? 0}%</div>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full transition-all ${projectState.status === "failed" ? "bg-red-500" : "bg-amber-400"}`}
                      style={{ width: `${projectState.progress ?? 0}%` }}
                    />
                  </div>
                  <div className="text-xs text-zinc-400 truncate">
                    {projectState.progress_message || projectState.status}
                  </div>
                  {projectState.status === "completed" && projectState.video_filename && (() => {
                    const asp = projectState.settings?.aspect || "16:9";
                    const aspClass = asp === "9:16" ? "aspect-[9/16] max-w-[360px] mx-auto"
                                     : asp === "1:1" ? "aspect-square max-w-[480px] mx-auto"
                                     : "aspect-video";
                    return (
                      <div className="mt-3">
                        <video
                          key={projectState.video_filename}
                          controls
                          className={`w-full ${aspClass} rounded-lg bg-black object-cover`}
                          poster={projectState.thumbnail_filename ? fileUrl(`thumbnails/${projectState.thumbnail_filename}`) : undefined}
                          data-testid="result-video"
                        >
                          <source src={fileUrl(`videos/${projectState.video_filename}`)} type="video/mp4" />
                        </video>
                        <a
                          href={fileUrl(`videos/${projectState.video_filename}`)}
                          download
                          className="mt-3 inline-block text-[11px] font-mono-tech uppercase tracking-wider text-amber-400 hover:text-amber-300"
                        >
                          Download video →
                        </a>
                      </div>
                    );
                  })()}
                  {projectState.status === "failed" && (
                    <div className="mt-2 text-xs text-red-300">
                      {projectState.error || "Onbekende fout"} — probeer een andere tier of neem contact op.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="w-full max-w-3xl">
              {attached.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2" data-testid="attached-strip">
                  {attached.map((a, i) => (
                    <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden border border-white/10 bg-[#121214] group">
                      <img src={a.url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeAttached(i)}
                        data-testid={`attached-remove-${i}`}
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition"
                      >
                        <X className="w-4 h-4 text-white" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-3xl border border-white/10 bg-[#121214] shadow-lg overflow-hidden">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                  placeholder="Beschrijf je video… (bv: 'Cinematische vastgoedvideo van een luxe villa met zeezicht voor Instagram Reels')"
                  rows={3}
                  className="w-full bg-transparent px-5 pt-4 pb-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none resize-none"
                  data-testid="chat-input"
                />
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-white/5 flex-wrap">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileRef} type="file" multiple
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                      className="hidden" onChange={onFilePick} data-testid="chat-file-input"
                    />
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="p-2 rounded-full text-zinc-400 hover:bg-white/5 hover:text-white transition"
                      title="Foto's toevoegen"
                      data-testid="chat-attach"
                    >
                      <Paperclip className="w-5 h-5" strokeWidth={1.75} />
                    </button>
                    <div
                      data-testid="chat-agent-badge"
                      className="px-2 py-1 rounded-full text-[10px] font-mono-tech uppercase tracking-wider text-amber-400 bg-amber-500/10 flex items-center gap-1"
                      title="Claude Sonnet agent handles tier, aspect, style and generation on your behalf"
                    >
                      <Sparkles className="w-3 h-3" strokeWidth={1.75} />
                      Claude agent
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {attached.length > 0 && (
                      <div className="text-[11px] font-mono-tech text-zinc-400 flex items-center gap-1">
                        <Coins className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
                        {attached.length} foto{attached.length === 1 ? "" : "'s"}
                      </div>
                    )}
                    <button
                      onClick={submit}
                      disabled={busy}
                      data-testid="chat-send"
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
                        busy ? "bg-white/10 text-zinc-500 cursor-not-allowed" : "bg-amber-500 text-black hover:bg-amber-400"
                      }`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" strokeWidth={2.25} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 mt-2 text-center font-mono-tech tracking-wider">
                <Sparkles className="inline w-3 h-3 text-amber-400 mr-1" strokeWidth={1.5} />
                MediaOthers · Studio · Powered by Claude
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
